import React, { useEffect, useState, useMemo } from "react";
import { getData, postData } from "../../services/FetchNodeservices";
import {
  FaPlus,
  FaMinus,
  FaTrash,
  FaUserPlus,
  FaExchangeAlt,
  FaSitemap,
  FaSearch,
  FaExpand,
  FaCompress,
  FaUserTie,
  FaUsers,
  FaEllipsisV
} from "react-icons/fa";
import { useSelector } from "react-redux";

export default function HierarchyOrgChart() {
  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [changeModal, setChangeModal] = useState(false);

  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [searchUser, setSearchUser] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [activeMenuId, setActiveMenuId] = useState(null);

  const [formData, setFormData] = useState({
    user_ids: [],
  });

  const user = useSelector((state) => state.user);

  useEffect(() => {
    fetchHierarchy();
    fetchUsers();

    const handleOutsideClick = () => setActiveMenuId(null);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  /* ================= FETCH ================= */

  const fetchHierarchy = async () => {
    setLoading(true);
    const res = await getData(`hirearchy/all/${user.id}`);

    if (res?.status) {
      const data = res.data || [];
      setTreeData(data);

      // Default expand all nodes recursively
      const autoExpanded = {};
      const markAllExpanded = (nodes) => {
        if (!nodes) return;
        nodes.forEach((n) => {
          autoExpanded[n.user_id] = true;
          if (n.children && n.children.length > 0) {
            markAllExpanded(n.children);
          }
        });
      };
      markAllExpanded(data);
      setExpandedNodes(autoExpanded);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    const res = await getData("hirearchy/users");
    if (res?.status) setUsers(res.data);
  };

  /* ================= TOGGLES ================= */

  const toggleNode = (id) =>
    setExpandedNodes((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () => {
    const allExp = {};
    const markAll = (nodes) => {
      if (!nodes) return;
      nodes.forEach((n) => {
        allExp[n.user_id] = true;
        if (n.children && n.children.length > 0) {
          markAll(n.children);
        }
      });
    };
    markAll(treeData);
    setExpandedNodes(allExp);
  };

  const collapseAll = () => {
    setExpandedNodes({});
  };

  /* ================= ADD MEMBER ================= */

  const openAddModal = (managerId) => {
    setSelectedManagerId(managerId);
    setSearchUser("");
    setFormData({ user_ids: [] });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.user_ids.length) return alert("Select at least one team member");

    await postData("hirearchy/add-member", {
      user_ids: formData.user_ids,
      reporting_manager_id: selectedManagerId,
    });

    setModalOpen(false);
    fetchHierarchy();
  };

  /* ================= CHANGE MANAGER ================= */

  const openChangeManager = (userId) => {
    setSelectedUserId(userId);
    setSearchUser("");
    setChangeModal(true);
  };

  const handleChangeManager = async (managerId) => {
    await postData(`hirearchy/update-member/${selectedUserId}`, {
      reporting_manager_id: managerId,
    });

    setChangeModal(false);
    fetchHierarchy();
  };

  /* ================= REMOVE ================= */

  const removeMember = async (id) => {
    if (!window.confirm("Are you sure you want to remove this team member from the hierarchy?")) return;
    await postData("hirearchy/remove-member", { user_id: id });
    fetchHierarchy();
  };

  /* ================= HELPERS & STATS ================= */

  const totalEmployeesCount = useMemo(() => {
    let count = 0;
    const countNodes = (nodes) => {
      if (!nodes) return;
      nodes.forEach((n) => {
        count++;
        if (n.children) countNodes(n.children);
      });
    };
    countNodes(treeData);
    return count;
  }, [treeData]);

  const countSubtree = (node) => {
    if (!node.children || node.children.length === 0) return 0;
    let total = node.children.length;
    node.children.forEach((c) => {
      total += countSubtree(c);
    });
    return total;
  };

  const avatar = (name, designation) => {
    const initials = (name || "E M")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    const isTopRole =
      designation?.toLowerCase().includes("director") ||
      designation?.toLowerCase().includes("president") ||
      designation?.toLowerCase().includes("ceo");

    const isManagerRole = designation?.toLowerCase().includes("manager");

    let colorStyle = "bg-gradient-to-tr from-sky-500 to-blue-600 text-white border-sky-400/40";
    if (isTopRole) {
      colorStyle = "bg-gradient-to-tr from-amber-500 to-yellow-400 text-slate-950 border-amber-300/60 shadow-lg shadow-amber-500/20 font-black";
    } else if (isManagerRole) {
      colorStyle = "bg-gradient-to-tr from-indigo-600 to-purple-600 text-white border-indigo-400/50 shadow-md shadow-indigo-500/20";
    }

    return (
      <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center font-black text-xs shadow-inner shrink-0 ${colorStyle}`}>
        {initials}
      </div>
    );
  };

  /* Permission Check */
  const canManage =
    user?.can_manage_hierarchy === 1 ||
    user?.designation_name === "Director" ||
    user?.designation_name === "Manager" ||
    user?.designation_name === "Vice President";

  /* ================= HORIZONTAL TREE NODE RECURSIVE RENDERER ================= */

  const renderHorizontalTreeNode = (node) => {
    const isExpanded = Boolean(expandedNodes[node.user_id]);
    const hasChildren = node.children && node.children.length > 0;
    const directReportsCount = node.children ? node.children.length : 0;
    const totalTeamCount = countSubtree(node);

    const isSearchMatch =
      searchQuery &&
      (node.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.designation_name?.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
      <div key={node.user_id} className="flex items-center select-none group/node">
        
        {/* NODE CARD WRAPPER */}
        <div className="relative">
          {/* NODE CARD */}
          <div
            className={`bg-slate-900/90 backdrop-blur-md border rounded-2xl p-3.5 w-64 transition-all duration-300 relative hover:-translate-y-0.5 shadow-xl shrink-0 ${
              isSearchMatch
                ? "border-amber-400 ring-4 ring-amber-400/30 bg-amber-950/20 shadow-lg shadow-amber-500/20 z-20"
                : "border-indigo-950 hover:border-indigo-500/60 hover:shadow-2xl hover:shadow-indigo-500/10"
            }`}
          >
            {/* LEFT ACCENT STRIP */}
            <div className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-gradient-to-b from-indigo-500 via-purple-500 to-indigo-500" />

            {isSearchMatch && (
              <span className="absolute -top-3 left-6 px-2.5 py-0.5 bg-amber-400 text-slate-950 font-black text-[9px] uppercase tracking-wider rounded-full shadow-md">
                Search Match
              </span>
            )}

            {/* THREE-DOTS CONFIG DROPDOWN BUTTON (TOP RIGHT) */}
            {canManage && (
              <div className="absolute top-3.5 right-3.5 z-20">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMenuId(activeMenuId === node.user_id ? null : node.user_id);
                  }}
                  className="text-slate-500 hover:text-slate-350 p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                  title="Node Actions"
                >
                  <FaEllipsisV className="w-2.5 h-2.5" />
                </button>

                {activeMenuId === node.user_id && (
                  <div className="absolute right-0 top-7 w-44 rounded-xl bg-slate-900 border border-slate-800 shadow-2xl py-1.5 z-30 animate-fadeIn text-slate-100">
                    <button
                      onClick={() => {
                        openAddModal(node.user_id);
                        setActiveMenuId(null);
                      }}
                      className="w-full text-left px-3.5 py-2 text-xs font-bold text-slate-300 hover:bg-indigo-600 hover:text-white transition flex items-center gap-2"
                    >
                      <FaUserPlus className="w-3 h-3 text-indigo-400" />
                      Add Direct Report
                    </button>

                    <button
                      onClick={() => {
                        openChangeManager(node.user_id);
                        setActiveMenuId(null);
                      }}
                      className="w-full text-left px-3.5 py-2 text-xs font-bold text-slate-300 hover:bg-indigo-600 hover:text-white transition flex items-center gap-2"
                    >
                      <FaExchangeAlt className="w-3 h-3 text-indigo-400" />
                      Change Manager
                    </button>

                    {node.user_id !== user.id && (
                      <button
                        onClick={() => {
                          removeMember(node.user_id);
                          setActiveMenuId(null);
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs font-bold text-rose-400 hover:bg-rose-600 hover:text-white transition flex items-center gap-2 border-t border-slate-800/40 mt-1 pt-2"
                      >
                        <FaTrash className="w-3 h-3 text-rose-450" />
                        Remove Member
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 pl-1 pr-6">
              {avatar(node.employee_name, node.designation_name)}

              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-100 text-sm tracking-tight leading-tight truncate" title={node.employee_name}>
                  {node.employee_name}
                </div>

                <div className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider bg-indigo-950/80 border border-indigo-900/60 text-indigo-300 px-2 py-0.5 rounded shadow-inner truncate max-w-full">
                  {node.designation_name || "Team Member"}
                </div>

                {directReportsCount > 0 && (
                  <div className="mt-1">
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-indigo-400 bg-indigo-950/40 px-1.5 py-0.5 rounded border border-indigo-900/40">
                      <FaUsers className="text-[9px]" />
                      {directReportsCount} Direct ({totalTeamCount} Team)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* EXPAND/COLLAPSE EXPANDER BUTTON (CENTERED ON RIGHT CONNECTOR STEM) */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(node.user_id)}
              className={`absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border flex items-center justify-center text-xs shadow-md transition-all z-15 ${
                isExpanded
                  ? "bg-slate-900 text-slate-350 border-slate-700 hover:bg-slate-800"
                  : "bg-indigo-650 text-white border-indigo-500 hover:bg-indigo-550 shadow-indigo-650/20 shadow-md"
              }`}
              title={isExpanded ? "Collapse Branch" : "Expand Branch"}
            >
              {isExpanded ? <FaMinus className="w-2.5 h-2.5" /> : <FaPlus className="w-2.5 h-2.5" />}
            </button>
          )}
        </div>

        {/* HORIZONTAL CONNECTOR LINES & CHILDREN BRANCHES (RIGHT) */}
        {isExpanded && hasChildren && (
          <div className="flex items-center">
            {/* HORIZONTAL CONNECTOR STEM FROM PARENT TO RIGHT */}
            <div className="h-0.5 w-6 bg-indigo-500/20 group-hover/node:bg-indigo-500/50 transition-colors duration-300 shrink-0" />

            {/* CHILDREN COLUMN */}
            <div className="flex flex-col justify-center gap-4 pl-6 relative">
              {node.children.map((child, index) => {
                const isFirst = index === 0;
                const isLast = index === node.children.length - 1;
                const isOnly = node.children.length === 1;

                return (
                  <div key={child.user_id} className="relative flex items-center py-1">
                    {/* VERTICAL CONNECTOR SEGMENT */}
                    {!isOnly && (
                      <div
                        className="absolute left-0 w-0.5 bg-indigo-500/20 group-hover/node:bg-indigo-500/40 transition-colors duration-300"
                        style={{
                          top: isFirst ? "50%" : "0%",
                          bottom: isLast ? "50%" : "0%",
                        }}
                      />
                    )}
                    {/* HORIZONTAL CONNECTOR LINE IN FROM SPINE TO CHILD CARD */}
                    <div className="h-0.5 w-6 bg-indigo-500/20 group-hover/node:bg-indigo-500/50 transition-colors duration-300 -ml-6 mr-0 shrink-0" />
                    {renderHorizontalTreeNode(child)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    );
  };

  /* ================= LOADING SCREEN ================= */

  if (loading)
    return (
      <div className="flex flex-col justify-center items-center min-h-[500px] gap-3 bg-slate-950 text-slate-400 font-bold">
        <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full" />
        <span className="text-xs uppercase tracking-widest font-black">Building Horizontal Tree Structure...</span>
      </div>
    );

  /* ================= MAIN INTERFACE ================= */

  return (
    <div className="p-4 md:p-6 bg-slate-950 text-slate-100 min-h-screen font-sans flex flex-col">
      
      {/* HEADER BAR */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5 pb-4 border-b border-slate-800/80 shrink-0">
        <div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight flex items-center gap-2.5 text-white">
            <FaSitemap className="text-indigo-400 w-6 h-6" /> Organization Chart
          </h1>
          <p className="text-xs font-semibold text-slate-400 mt-0.5">
            Visualize reporting structures, manage team members, or change reporting managers
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-black px-3.5 py-1.5 rounded-xl bg-indigo-950/80 border border-indigo-800/60 text-indigo-300 flex items-center gap-1.5">
            <FaUserTie className="text-indigo-400" />
            {totalEmployeesCount} Total Members
          </span>
        </div>
      </div>

      {/* MINIMAL CLEAN SEARCH & EXPAND CONTROLS BAR */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-3 mb-5 flex flex-wrap gap-3 items-center justify-between shadow-lg shrink-0">
        
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search employee by name or designation rank..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 text-xs focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-950 placeholder-slate-500 transition-all font-semibold"
          />
        </div>

        {/* Expand / Collapse Actions */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={expandAll}
            className="px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs font-black text-slate-300 hover:text-white hover:border-slate-700 transition-all flex items-center gap-1.5"
            title="Expand All Nodes"
          >
            <FaExpand className="text-[10px]" /> Expand All
          </button>

          <button
            onClick={collapseAll}
            className="px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs font-black text-slate-300 hover:text-white hover:border-slate-700 transition-all flex items-center gap-1.5"
            title="Collapse All Nodes"
          >
            <FaCompress className="text-[10px]" /> Collapse All
          </button>
        </div>

      </div>

      {/* HORIZONTAL TREE SCROLLABLE CANVAS CONTAINER */}
      <div 
        className="bg-slate-950 border border-slate-900 rounded-3xl overflow-auto flex-1 max-h-[calc(100vh-230px)] shadow-2xl p-8 lg:p-12 relative"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(99, 102, 241, 0.04) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(99, 102, 241, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px'
        }}
      >
        {treeData.length === 0 ? (
          <div className="py-20 text-center text-slate-500 font-bold flex flex-col items-center justify-center h-full gap-2">
            <FaSitemap className="text-4xl text-slate-700" />
            <span>No hierarchy records found in organizational structure.</span>
          </div>
        ) : (
          <div className="inline-block min-w-max">
            <div className="flex flex-col gap-12 items-start min-w-max">
              {treeData.map((root) => renderHorizontalTreeNode(root))}
            </div>
          </div>
        )}
      </div>

      {/* ================= ADD MEMBER MODAL ================= */}
      {modalOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl p-6 w-full max-w-md animate-fadeIn text-slate-100">
            <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <FaUserPlus className="text-indigo-400 text-base" /> Add Team Members
            </h3>

            <div className="relative mb-4">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
              <input
                type="text"
                placeholder="Search team candidates..."
                value={searchUser}
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-500 font-semibold"
                onChange={(e) => setSearchUser(e.target.value)}
              />
            </div>

            <div className="max-h-56 overflow-y-auto border border-slate-800 rounded-2xl p-2 space-y-1 mb-6 bg-slate-950/50">
              {users
                .filter((u) =>
                  `${u.first_name || ""} ${u.last_name || ""}`
                    .toLowerCase()
                    .includes(searchUser.toLowerCase())
                )
                .map((u) => {
                  const isChecked = formData.user_ids.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-slate-800/80 transition ${
                        isChecked ? "bg-indigo-950/60 border border-indigo-800/60" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData((prev) => ({
                              user_ids: [...prev.user_ids, u.id],
                            }));
                          } else {
                            setFormData((prev) => ({
                              user_ids: prev.user_ids.filter((id) => id !== u.id),
                            }));
                          }
                        }}
                        className="rounded border-slate-700 bg-slate-950 text-indigo-500 focus:ring-indigo-500 h-4 w-4"
                      />
                      <span className="text-xs font-bold text-slate-200">
                        {u.first_name} {u.last_name}
                      </span>
                    </label>
                  );
                })}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-extrabold transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-extrabold shadow-md shadow-indigo-600/20 transition"
              >
                Add Selected Members
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= CHANGE MANAGER MODAL ================= */}
      {changeModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl p-6 w-full max-w-md animate-fadeIn text-slate-100">
            <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
              <FaExchangeAlt className="text-indigo-400 text-base" /> Change Reporting Manager
            </h3>

            <div className="relative mb-4">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
              <input
                type="text"
                placeholder="Search target manager..."
                value={searchUser}
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-500 font-semibold"
                onChange={(e) => setSearchUser(e.target.value)}
              />
            </div>

            <div className="max-h-56 overflow-y-auto border border-slate-800 rounded-2xl p-2 space-y-1 mb-6 bg-slate-950/50">
              {users
                .filter((u) =>
                  `${u.first_name || ""} ${u.last_name || ""}`
                    .toLowerCase()
                    .includes(searchUser.toLowerCase())
                )
                .map((u) => (
                  <div
                    key={u.id}
                    onClick={() => handleChangeManager(u.id)}
                    className="px-4 py-2.5 hover:bg-indigo-950/60 text-xs font-bold text-slate-200 rounded-xl cursor-pointer transition flex items-center gap-2"
                  >
                    <span>👤</span> {u.first_name} {u.last_name}
                  </div>
                ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setChangeModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-extrabold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}